import React, { useEffect, useRef, useState } from 'react'
import { loadPaths, savePaths } from '../vision/pathUtils'
import { drawPaths } from '../vision/pathRenderer'
import { acquireCameraStream, stopCameraStream } from '../vision/cameraConfig'
import styles from './AiCodeConfigure.module.css'

// Draw multi-point crossing PATHS on a live camera preview. Click empty space
// to add a point to the active path; drag a point to move it. Each named path
// becomes a selectable "line" (with Forward/Backward direction) in the decision
// panel. Points are stored normalized (0-1) in video space so they map to the
// Execute camera regardless of element size / object-fit.
const rnd = () => Math.random().toString(36).slice(2, 8)

const LinesEditor = () => {
    const videoRef = useRef(null)
    const canvasRef = useRef(null)
    const wrapRef = useRef(null)
    const streamRef = useRef(null)
    const dragRef = useRef(null)
    const rafRef = useRef(null)
    // Live cursor in normalized (0-1) video space, for the ghost preview curve.
    const cursorRef = useRef(null)

    const [paths, setPaths] = useState(() => {
        const p = loadPaths()
        return p.length ? p : [{ id: rnd(), name: 'Path 1', points: [] }]
    })
    const [activeId, setActiveId] = useState(() => null)
    // Path ids the user has finished (double-clicked): no more points get added
    // and the ghost preview stops for them. Session-only (not persisted).
    const [doneIds, setDoneIds] = useState(() => new Set())
    const pathsRef = useRef(paths)
    const activeIdRef = useRef(activeId)
    const doneIdsRef = useRef(doneIds)
    useEffect(() => {
        pathsRef.current = paths
        savePaths(paths)
    }, [paths])
    useEffect(() => {
        activeIdRef.current = activeId
    }, [activeId])
    useEffect(() => {
        doneIdsRef.current = doneIds
    }, [doneIds])
    const markDone = (id) => setDoneIds((s) => new Set(s).add(id))
    const unmarkDone = (id) =>
        setDoneIds((s) => {
            if (!s.has(id)) return s
            const n = new Set(s)
            n.delete(id)
            return n
        })
    useEffect(() => {
        if (!activeId && paths[0]) setActiveId(paths[0].id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Start the configured camera at the chosen aspect ratio (both come from the
    // Inputs panel via cameraConfig / sessionStorage).
    useEffect(() => {
        const deviceId = sessionStorage.getItem('aiCameraDeviceId') || undefined
        ;(async () => {
            try {
                // Uses the paired phone camera when it's the chosen source.
                const stream = await acquireCameraStream(deviceId)
                streamRef.current = stream
                if (videoRef.current) {
                    videoRef.current.srcObject = stream
                    await videoRef.current.play().catch(() => {})
                }
            } catch (e) {
                /* camera unavailable */
            }
        })()
        return () => {
            stopCameraStream(streamRef.current)
        }
    }, [])

    // object-fit: contain transform between element px and normalized video.
    const geom = () => {
        const v = videoRef.current
        const wrap = wrapRef.current
        if (!v || !wrap) return null
        const r = wrap.getBoundingClientRect()
        const vw = v.videoWidth || r.width
        const vh = v.videoHeight || r.height
        const scale = Math.min(r.width / vw, r.height / vh)
        const dispW = vw * scale
        const dispH = vh * scale
        return {
            r,
            dispW,
            dispH,
            offX: (r.width - dispW) / 2,
            offY: (r.height - dispH) / 2,
        }
    }
    const toPx = (n, g) => ({
        x: n.x * g.dispW + g.offX,
        y: n.y * g.dispH + g.offY,
    })
    const toNorm = (clientX, clientY, g) => ({
        x: Math.min(1, Math.max(0, (clientX - g.r.left - g.offX) / g.dispW)),
        y: Math.min(1, Math.max(0, (clientY - g.r.top - g.offY) / g.dispH)),
    })

    // Continuous redraw so the overlay tracks video load / resize.
    useEffect(() => {
        const draw = () => {
            rafRef.current = requestAnimationFrame(draw)
            const canvas = canvasRef.current
            const wrap = wrapRef.current
            const g = geom()
            if (!canvas || !wrap || !g) return
            const dpr = window.devicePixelRatio || 1
            const w = Math.round(g.r.width)
            const h = Math.round(g.r.height)
            if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
                canvas.width = w * dpr
                canvas.height = h * dpr
            }
            canvas.style.width = `${w}px`
            canvas.style.height = `${h}px`
            const ctx = canvas.getContext('2d')
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
            ctx.clearRect(0, 0, w, h)

            // Only preview the ghost curve when hovering (not dragging a point)
            // and the active path already has at least one point to extend from.
            const active = pathsRef.current.find(
                (p) => p.id === activeIdRef.current
            )
            const ghost =
                dragRef.current == null &&
                cursorRef.current &&
                active &&
                active.points.length &&
                !doneIdsRef.current.has(active.id)
                    ? cursorRef.current
                    : null
            drawPaths(ctx, pathsRef.current, (n) => toPx(n, g), {
                activeId: activeIdRef.current,
                ghostCursor: ghost,
                showIndices: true,
            })
        }
        rafRef.current = requestAnimationFrame(draw)
        return () => rafRef.current && cancelAnimationFrame(rafRef.current)
    }, [])

    const activePath = paths.find((p) => p.id === activeId)

    const hitPoint = (norm, g) => {
        if (!activePath) return -1
        for (let i = 0; i < activePath.points.length; i++) {
            const pt = toPx(activePath.points[i], g)
            const c = toPx(norm, g)
            if (Math.hypot(pt.x - c.x, pt.y - c.y) < 14) return i
        }
        return -1
    }

    const onPointerDown = (e) => {
        const g = geom()
        if (!g || !activePath) return
        const norm = toNorm(e.clientX, e.clientY, g)
        const hit = hitPoint(norm, g)
        if (hit >= 0) {
            dragRef.current = hit
            window.addEventListener('pointermove', onPointerMove)
            window.addEventListener('pointerup', onPointerUp)
            return
        }
        // Once a path is finished, empty-space clicks no longer add points.
        if (doneIds.has(activeId)) return
        // Add a new point to the active path.
        setPaths((prev) =>
            prev.map((p) =>
                p.id === activeId
                    ? { ...p, points: [...p.points, norm] }
                    : p
            )
        )
    }
    // Double-click finishes the active path: no more points, no ghost preview.
    const onDoubleClick = () => {
        if (!activePath || activePath.points.length < 2) return
        const pts = activePath.points
        // Drop the duplicate final point the double-click's first press added.
        if (pts.length >= 3) {
            const a = pts[pts.length - 1]
            const b = pts[pts.length - 2]
            if (Math.hypot(a.x - b.x, a.y - b.y) < 0.03)
                setPaths((prev) =>
                    prev.map((p) =>
                        p.id === activeId
                            ? { ...p, points: p.points.slice(0, -1) }
                            : p
                    )
                )
        }
        markDone(activeId)
        cursorRef.current = null
    }
    const onPointerMove = (e) => {
        const g = geom()
        if (!g || dragRef.current == null) return
        const norm = toNorm(e.clientX, e.clientY, g)
        setPaths((prev) =>
            prev.map((p) => {
                if (p.id !== activeIdRef.current) return p
                const points = p.points.slice()
                points[dragRef.current] = norm
                return { ...p, points }
            })
        )
    }
    const onPointerUp = () => {
        dragRef.current = null
        window.removeEventListener('pointermove', onPointerMove)
        window.removeEventListener('pointerup', onPointerUp)
    }
    // Track the cursor over the stage so the ghost curve can follow it live.
    const onStageHover = (e) => {
        const g = geom()
        if (!g) return
        cursorRef.current = toNorm(e.clientX, e.clientY, g)
    }
    const onStageLeave = () => {
        cursorRef.current = null
    }

    const addPath = () => {
        const id = rnd()
        setPaths((prev) => [
            ...prev,
            { id, name: `Path ${prev.length + 1}`, points: [] },
        ])
        setActiveId(id)
    }
    const undoPoint = () => {
        unmarkDone(activeId)
        setPaths((prev) =>
            prev.map((p) =>
                p.id === activeId
                    ? { ...p, points: p.points.slice(0, -1) }
                    : p
            )
        )
    }
    const clearPath = () => {
        unmarkDone(activeId)
        setPaths((prev) =>
            prev.map((p) => (p.id === activeId ? { ...p, points: [] } : p))
        )
    }
    const deletePath = (id) => {
        unmarkDone(id)
        setPaths((prev) => {
            const next = prev.filter((p) => p.id !== id)
            if (id === activeId) setActiveId(next[0]?.id || null)
            return next.length
                ? next
                : [{ id: rnd(), name: 'Path 1', points: [] }]
        })
    }
    const renamePath = (id, name) =>
        setPaths((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)))

    return (
        <div className={styles.linesWrap}>
            <div
                className={styles.linesStage}
                ref={wrapRef}
                onPointerDown={onPointerDown}
                onPointerMove={onStageHover}
                onPointerLeave={onStageLeave}
                onDoubleClick={onDoubleClick}
            >
                <video
                    ref={videoRef}
                    className={styles.linesVideo}
                    autoPlay
                    playsInline
                    muted
                />
                <canvas ref={canvasRef} className={styles.linesCanvas} />
            </div>

            <div className={styles.linesPanel}>
                <p className={styles.linesHint}>
                    Move the cursor to preview the path, then click to drop a
                    point. Drag a point to move it, and double-click to finish.
                    The arrows show the forward direction (start → end).
                </p>
                <div className={styles.linesList}>
                    {paths.map((p) => (
                        <div
                            key={p.id}
                            className={`${styles.linesItem} ${
                                p.id === activeId ? styles.linesItemActive : ''
                            }`}
                            onClick={() => setActiveId(p.id)}
                        >
                            <input
                                className={styles.linesName}
                                value={p.name}
                                onChange={(e) => renamePath(p.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span className={styles.linesCount}>
                                {p.points.length} pts
                            </span>
                            <button
                                className={styles.linesDel}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    deletePath(p.id)
                                }}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
                <div className={styles.linesBtns}>
                    <button className={styles.linesBtn} onClick={addPath}>
                        + Add Path
                    </button>
                    <button className={styles.linesBtn} onClick={undoPoint}>
                        Undo Point
                    </button>
                    <button className={styles.linesBtn} onClick={clearPath}>
                        Clear
                    </button>
                </div>
            </div>
        </div>
    )
}

export default LinesEditor
